import type { VehicleReservation } from "@/types/vehicleReservation";

/** 분 단위 시간 구간 [start, end) */
export type Interval = [number, number];

const ALL_DAY: Interval = [0, 24 * 60];

export const isAllDayDrivingTime = (drivingTime?: string | null): boolean =>
  drivingTime === "전일" || drivingTime?.replace(/\s/g, "") === "00:00~24:00";

const toMinutes = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/**
 * drivingTime 문자열을 분 단위 구간 목록으로 변환.
 * 지원: "전일", "09:00 ~ 18:00", "08:00~15:00", "06:00 ~ 09:00, 12:00 ~ 24:00"
 * 파싱 불가능한 값은 보수적으로 전일 점유로 취급 (백엔드와 동일한 규칙).
 */
export const parseDrivingTime = (drivingTime?: string | null): Interval[] => {
  if (!drivingTime || isAllDayDrivingTime(drivingTime)) return [ALL_DAY];

  const intervals: Interval[] = [];
  for (const part of drivingTime.split(",")) {
    const range = part.split("~");
    if (range.length !== 2) return [ALL_DAY];
    const start = toMinutes(range[0]);
    const end = toMinutes(range[1]);
    if (start === null || end === null || end <= start) return [ALL_DAY];
    intervals.push([start, end]);
  }
  return intervals.length > 0 ? intervals : [ALL_DAY];
};

const overlaps = (a: Interval[], b: Interval[]): boolean =>
  a.some(([s1, e1]) => b.some(([s2, e2]) => s1 < e2 && s2 < e1));

/** 선택 차량의 예약과 요청 시간대가 겹치는지 */
export const hasTimeConflict = (
  reservations: Pick<VehicleReservation, "drivingTime">[],
  drivingTime: string,
): boolean => {
  const requested = parseDrivingTime(drivingTime);

  return reservations.some(r =>
    overlaps(parseDrivingTime(r.drivingTime), requested),
  );
};

/** 선택 차량이 그날 전일 예약을 갖고 있는지 (캘린더 빨강 / 추가 예약 불가) */
export const hasAllDayReservation = (
  reservations: Pick<VehicleReservation, "drivingTime">[],
): boolean => reservations.some(r => isAllDayDrivingTime(r.drivingTime));
